import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface CustomerFieldConfig {
  id: string;
  field_name: string;
  display_name: string;
  field_type: string;
  is_mandatory: boolean;
  is_active: boolean;
  validation_rules: any;
  help_text?: string;
  default_value?: string;
  field_group: string;
  sort_order: number;
  customer_id?: string;
  priority: 'customer' | 'organization' | 'system';
}

export const useCustomerFieldConfigs = (customerId?: string) => {
  const { user } = useAuth();

  const configsQuery = useQuery({
    queryKey: ['customer-field-configs', user?.id, customerId],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      // Build query to handle null customerId properly
      let query = supabase
        .from('field_validation_configs')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);

      // Handle customer_id filtering safely
      if (customerId) {
        query = query.in('customer_id', [customerId, null]);
      } else {
        query = query.is('customer_id', null);
      }

      const { data, error } = await query
        .order('customer_id', { ascending: false }) // Customer-specific first
        .order('sort_order');

      if (error) throw error;

      // Process configs to apply priority system
      const configMap = new Map<string, CustomerFieldConfig>();
      
      data?.forEach(config => {
        const priority = config.customer_id ? 'customer' : 'organization';
        const enhancedConfig: CustomerFieldConfig = {
          ...config,
          priority
        };

        const existing = configMap.get(config.field_name);
        
        // Only replace if new config has higher priority
        if (!existing || 
           (priority === 'customer' && existing.priority !== 'customer')) {
          configMap.set(config.field_name, enhancedConfig);
        }
      });

      return Array.from(configMap.values());
    },
    enabled: !!user?.id,
  });

  const mandatoryConfigs = configsQuery.data?.filter(config => 
    config.is_mandatory && config.is_active
  ) || [];

  const configsByGroup = configsQuery.data?.reduce((acc, config) => {
    const group = config.field_group || 'general';
    if (!acc[group]) acc[group] = [];
    acc[group].push(config);
    return acc;
  }, {} as Record<string, CustomerFieldConfig[]>) || {};

  const getFieldConfig = (fieldName: string) => {
    return configsQuery.data?.find(config => config.field_name === fieldName);
  };

  const validateField = (fieldName: string, value: any) => {
    const config = getFieldConfig(fieldName);
    if (!config) return { valid: true, errors: [] };

    const errors: string[] = [];

    // Check if mandatory field is empty
    if (config.is_mandatory && (!value || value === '')) {
      errors.push(`${config.display_name} is required`);
    }

    // Apply validation rules
    if (value && config.validation_rules) {
      const rules = config.validation_rules;
      
      if (rules.min_length && value.length < rules.min_length) {
        errors.push(`${config.display_name} must be at least ${rules.min_length} characters`);
      }
      
      if (rules.max_length && value.length > rules.max_length) {
        errors.push(`${config.display_name} must be at most ${rules.max_length} characters`);
      }
      
      if (rules.pattern && !new RegExp(rules.pattern).test(value)) {
        errors.push(`${config.display_name} format is invalid`);
      }
      
      if (rules.min_value && parseFloat(value) < rules.min_value) {
        errors.push(`${config.display_name} must be at least ${rules.min_value}`);
      }
      
      if (rules.max_value && parseFloat(value) > rules.max_value) {
        errors.push(`${config.display_name} must be at most ${rules.max_value}`);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  const validateAllFields = (data: Record<string, any>) => {
    const validationResults: Record<string, { valid: boolean; errors: string[] }> = {};
    let allValid = true;
    let completedMandatory = 0;
    const totalMandatory = mandatoryConfigs.length;

    mandatoryConfigs.forEach(config => {
      const result = validateField(config.field_name, data[config.field_name]);
      validationResults[config.field_name] = result;
      
      if (!result.valid) {
        allValid = false;
      } else if (config.is_mandatory && data[config.field_name]) {
        completedMandatory++;
      }
    });

    return {
      allValid,
      validationResults,
      completedMandatory,
      totalMandatory,
      completionPercentage: totalMandatory > 0 ? (completedMandatory / totalMandatory) * 100 : 100
    };
  };

  return {
    configs: configsQuery.data || [],
    mandatoryConfigs,
    configsByGroup,
    isLoading: configsQuery.isLoading,
    error: configsQuery.error,
    getFieldConfig,
    validateField,
    validateAllFields,
    refetch: configsQuery.refetch
  };
};